import { faCheck, faEraser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StyledInput } from '@ui';
import Dropdown from '@ui/Dropdown';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, SelectFilter } from '..';
import { FilterState } from '../DataTable';
import useSmartFilterValueStore from '../hooks/useSmartFilterValueStore';

interface Props {
    filters?: Filter[];
    filter: FilterState;
    onFilterChange: (filter: FilterState) => void;
}

const DEBOUNCE_DELAY = 300;

export default function SmartFilterComponent({ filters, filter, onFilterChange }: Props) {
    const { t } = useTranslation('datatable');
    const values = useSmartFilterValueStore((state) => state.values);
    const setValue = useSmartFilterValueStore((state) => state.setValue);
    const filterRef = useRef(filter);

    useEffect(() => {
        filterRef.current = filter;
    }, [filter]);

    const handleUpdateFilter = useMemo(
        () =>
            debounce((filterKey: string, value: any) => {
                const currentFilter = filterRef.current;
                const [key, rangePart] = filterKey.split(':');
                const filterType = filters?.find((f) => f.filterKey === key)?.filterType;
                if (!filterType) return;

                switch (filterType) {
                    case 'numeric': {
                        onFilterChange({
                            ...currentFilter,
                            [key]: {
                                ...((currentFilter?.[key] as {}) || {}),
                                [rangePart]: value === '' ? undefined : Number(value)
                            } as {
                                from: number | undefined;
                                to: number | undefined;
                            }
                        });
                        break;
                    }

                    case 'date': {
                        // Same {from, to} shape as numeric, but the value stays the ISO string the
                        // date input produces — the backend parses it, Number() would destroy it.
                        onFilterChange({
                            ...currentFilter,
                            [key]: {
                                ...((currentFilter?.[key] as {}) || {}),
                                [rangePart]: value === '' ? undefined : value
                            } as {
                                from: string | undefined;
                                to: string | undefined;
                            }
                        });
                        break;
                    }

                    default: {
                        onFilterChange({
                            ...currentFilter,
                            [key]: { value: value === '' ? undefined : value }
                        });
                        break;
                    }
                }
            }, DEBOUNCE_DELAY),
        [onFilterChange, filters]
    );

    useEffect(() => {
        return () => {
            handleUpdateFilter.cancel();
        };
    }, [handleUpdateFilter]);

    const handleChange = useCallback(
        (event) => {
            const { name, value } = event.target;
            setValue(name, value);
            handleUpdateFilter(name, value);
        },
        [handleUpdateFilter]
    );

    const renderFilter = (filter: Filter | SelectFilter) => {
        switch (filter.filterType) {
            case 'text':
                return (
                    <StyledInput
                        className="tw-min-w-[300px]"
                        bordered
                        placeholder={`Suche nach ${filter.filterName}`}
                        name={filter.filterKey}
                        value={values[filter.filterKey] || ''}
                        flush
                        onChange={handleChange}
                    />
                );
            case 'numeric':
                return (
                    <div className="tw-flex tw-min-w-[500px]">
                        <StyledInput
                            fullWidth
                            containerClassName="tw-rounded-tr-none tw-rounded-br-none tw-border-r-0"
                            type="number"
                            bordered
                            labelLeft="Ab"
                            placeholder={filter.filterName}
                            name={`${filter.filterKey}:from`}
                            value={values[`${filter.filterKey}:from`] || ''}
                            flush
                            onChange={handleChange}
                        />
                        <StyledInput
                            fullWidth
                            containerClassName="tw-rounded-tl-none tw-rounded-bl-none tw-border-l-0"
                            type="number"
                            bordered
                            labelLeft="Bis"
                            placeholder={filter.filterName}
                            name={`${filter.filterKey}:to`}
                            value={values[`${filter.filterKey}:to`] || ''}
                            flush
                            onChange={handleChange}
                        />
                    </div>
                );
            case 'date':
                return (
                    <div className="tw-flex tw-min-w-[420px]">
                        <StyledInput
                            fullWidth
                            containerClassName="tw-rounded-tr-none tw-rounded-br-none tw-border-r-0"
                            type="date"
                            bordered
                            labelLeft={t('filters.rangeFrom')}
                            name={`${filter.filterKey}:from`}
                            value={values[`${filter.filterKey}:from`] || ''}
                            flush
                            onChange={handleChange}
                        />
                        <StyledInput
                            fullWidth
                            containerClassName="tw-rounded-tl-none tw-rounded-bl-none tw-border-l-0"
                            type="date"
                            bordered
                            labelLeft={t('filters.rangeTo')}
                            name={`${filter.filterKey}:to`}
                            value={values[`${filter.filterKey}:to`] || ''}
                            flush
                            onChange={handleChange}
                        />
                    </div>
                );
            case 'select': {
                const options = (filter as SelectFilter).options;
                if (!options) return null;
                const selectedLabel = options.find(
                    (option) => String(option.value) === String(values[filter.filterKey])
                )?.label;
                return (
                    <Dropdown
                        placement="bottom"
                        size="sm"
                        label={filter.filterName + (selectedLabel ? `: ${selectedLabel}` : '')}
                    >
                        {options.map((option) => (
                            <Dropdown.Item
                                label={option.label}
                                key={option.value}
                                onClick={() => {
                                    setValue(filter.filterKey, option.value);
                                    handleUpdateFilter(filter.filterKey, option.value);
                                }}
                            />
                        ))}
                        <Dropdown.Item
                            color="warning"
                            icon={<FontAwesomeIcon icon={faEraser} />}
                            label={t('filters.reset')}
                            onClick={() => {
                                setValue(filter.filterKey, undefined);
                                handleUpdateFilter(filter.filterKey, undefined);
                            }}
                        />
                    </Dropdown>
                );
            }

            /*
             * The same control as `select`, except a click is a toggle rather
             * than an answer. Without this case the switch fell through to
             * `default: return null` — a filter declared in `smartFilterKeys`
             * and given options simply never appeared, silently, which is how
             * the participant list came to offer a weekday and a group filter
             * that nobody could see.
             *
             * Writes `{ value: [...] }`, which is the shape the drawer's own
             * `SelectMultipleFilter` writes and the backend reads as `whereIn`.
             * The two controls edit one filter and must not disagree about it.
             */
            case 'select-multiple': {
                const options = (filter as SelectFilter).options;
                if (!options) return null;

                const current = values[filter.filterKey];
                const selected = Array.isArray(current) ? current.map(String) : [];
                const isSelected = (value: string | number) => selected.includes(String(value));
                const chosen = options.filter((option) => isSelected(option.value));
                // One name fits on a chip; three do not, and showing only the
                // first would name a filter that is not the one in force.
                const summary =
                    1 === chosen.length ? chosen[0].label : t('filters.selectedCount', { count: chosen.length });

                const toggle = (value: string | number) => {
                    const key = String(value);
                    const next = isSelected(value) ? selected.filter((entry) => entry !== key) : [...selected, key];

                    setValue(filter.filterKey, next);
                    // Nothing ticked means no filter, not a filter that matches
                    // nothing: an empty array reaches the backend as `whereIn ()`
                    // and would empty the table instead of filling it.
                    handleUpdateFilter(filter.filterKey, next.length > 0 ? next : undefined);
                };

                return (
                    <Dropdown
                        placement="bottom"
                        size="sm"
                        // Ticking a second weekday must not mean opening the menu again.
                        closeOnSelect={false}
                        label={filter.filterName + (chosen.length > 0 ? `: ${summary}` : '')}
                    >
                        {options.map((option) => (
                            <Dropdown.Item
                                key={option.value}
                                label={option.label}
                                color={isSelected(option.value) ? 'success' : 'light'}
                                // Kept in the layout when unticked so the labels
                                // of ticked and unticked rows start at the same x.
                                icon={
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className={isSelected(option.value) ? undefined : 'tw-invisible'}
                                    />
                                }
                                onClick={() => toggle(option.value)}
                            />
                        ))}
                        <Dropdown.Item
                            color="warning"
                            icon={<FontAwesomeIcon icon={faEraser} />}
                            label={t('filters.reset')}
                            onClick={() => {
                                setValue(filter.filterKey, []);
                                handleUpdateFilter(filter.filterKey, undefined);
                            }}
                        />
                    </Dropdown>
                );
            }

            default:
                return null;
        }
    };

    if (!filters || filters.length === 0) return null;

    return <div className="tw-flex tw-flex-wrap tw-gap-2">{filters?.map(renderFilter)}</div>;
}
